import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, CheckCircle2, CreditCard, Clock } from "lucide-react";

const ST = {
  aberta: { label: "Aberta", cls: "bg-aj-amber-soft text-aj-orange-hover" },
  paga: { label: "Paga", cls: "bg-aj-success text-white" },
  isenta: { label: "Isenta", cls: "bg-aj-surface-2 text-aj-text-muted" },
};
const MOTIVO = {
  gratuidade_6_meses: "6 meses grátis",
  sem_atendimentos_no_mes: "Sem atendimentos",
  faturamento_abaixo_de_150: "Faturou ≤ R$150",
  sem_receita_no_mes: "Sem receita",
};

function fmtPeriodo(p) {
  if (!p) return "—";
  const [ano, mes] = p.split("-");
  const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${nomes[Number(mes) - 1]}/${ano}`;
}

export default function MinhasFaturas() {
  const [data, setData] = useState(null);
  const [pagando, setPagando] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const pollRef = useRef(false);

  const load = useCallback(() => {
    api.get("/me/faturas-saas")
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    const fid = searchParams.get("fatura_paga");
    if (!fid || pollRef.current) return;
    pollRef.current = true;
    let tent = 0;
    const poll = async () => {
      tent++;
      try {
        const r = await api.get(`/saas/faturas/${fid}/pagamento-status`);
        if (r.data.status === "paga") {
          toast.success("Pagamento confirmado! Obrigado.");
          setSearchParams({});
          load();
          return;
        }
      } catch { /* continua */ }
      if (tent < 6) setTimeout(poll, 2000);
      else {
        toast.info("Pagamento em processamento. Atualize a página em instantes.");
        setSearchParams({});
      }
    };
    poll();
  }, [searchParams, setSearchParams, load]);

  const pagar = async (f) => {
    setPagando(f.id);
    try {
      const r = await api.post(`/saas/faturas/${f.id}/checkout`, {
        origin_url: window.location.origin + window.location.pathname,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPagando(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="minhas-faturas-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Financeiro SaaS</div>
        <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Minhas Faturas</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Faturas mensais de uso da plataforma MontaJá. Pague com cartão via checkout seguro (Stripe).
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={<Wallet size={18} />} label="Em aberto" v={`R$ ${data.resumo.em_aberto.toFixed(2)}`} destaque testid="mf-emaberto" />
          <Kpi icon={<Clock size={18} />} label="Faturas abertas" v={data.resumo.qtd_abertas} testid="mf-abertas" />
          <Kpi icon={<CheckCircle2 size={18} />} label="Pagas" v={data.resumo.pagas} testid="mf-pagas" />
          <Kpi icon={<Wallet size={18} />} label="Total" v={data.resumo.total} testid="mf-total" />
        </div>
      )}

      <div className="aj-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-aj-surface-2 border-b border-[#E2E8F0]">
            <tr><Th>Mês de referência</Th><Th>Base de cálculo</Th><Th>Valor</Th><Th>Status</Th><Th>Ação</Th></tr>
          </thead>
          <tbody>
            {(!data || data.faturas.length === 0) && (
              <tr><td colSpan={5} className="p-10 text-center text-aj-text-muted" data-testid="mf-vazio">
                Nenhuma fatura por enquanto. As faturas são geradas automaticamente todo dia 1º do mês.
              </td></tr>
            )}
            {data?.faturas.map(f => {
              const s = ST[f.status] || ST.aberta;
              return (
                <tr key={f.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-aj-surface transition-colors" data-testid={`mf-row-${f.id}`}>
                  <Td className="font-semibold capitalize">{fmtPeriodo(f.periodo)}</Td>
                  <Td className="text-xs text-aj-text-muted">
                    {f.tipo === "empresa" && <>{f.qtd_atendimentos} atend. · mensalidade R$ {f.mensalidade?.toFixed(2)} + R$ {f.variavel?.toFixed(2)}</>}
                    {f.tipo === "montador" && <>{f.atendimentos_mes} atend. · faturou R$ {f.faturamento_mes?.toFixed(2)}</>}
                    {f.tipo === "montadora" && <>receita R$ {f.receita_mes?.toFixed(2)} × {f.percentual}%</>}
                    {f.motivo_isencao && <span className="block text-aj-orange font-semibold">{MOTIVO[f.motivo_isencao] || f.motivo_isencao}</span>}
                  </Td>
                  <Td className="font-mono font-bold">{f.valor_total > 0 ? `R$ ${f.valor_total.toFixed(2)}` : "—"}</Td>
                  <Td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${s.cls}`}>{s.label}</span></Td>
                  <Td>
                    {f.status === "aberta" && (
                      <button onClick={() => pagar(f)} disabled={pagando === f.id}
                        className="btn-aj-primary text-xs !py-2 !px-3 disabled:opacity-60"
                        data-testid={`mf-pagar-${f.id}`}>
                        <CreditCard size={13} /> {pagando === f.id ? "Abrindo…" : "Pagar agora"}
                      </button>
                    )}
                    {f.status === "paga" && f.paga_em && (
                      <span className="text-xs text-aj-text-muted">Paga em {new Date(f.paga_em).toLocaleDateString("pt-BR")}</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon, label, v, destaque, testid }) {
  return (
    <div className={`aj-card p-5 ${destaque ? "border-l-4 border-l-[#FF6700]" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-aj-orange">{icon}
        <span className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</span>
      </div>
      <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-2">{v}</div>
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
